import type { Placement, PositioningStrategy, State } from '@popperjs/core';
import type { SpringValues } from '@react-spring/web';
import type React from 'react';
import type { AriaAttributes, CSSProperties, FocusEventHandler, MouseEventHandler, ReactNode } from 'react';
import type { Modifier } from 'react-popper';
import type { ReMap } from '../../utils/types';
export interface PopoverStateProps {
  closeOnClickOutside?: boolean;
  onClickOutside?: (e: MouseEvent | TouchEvent) => void;
  trigger?: 'click' | 'hover' | '';
  isSmall?: boolean;
  usePortal?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  placement?: Placement;
  /** Delay before opening and closing the popover when the mouse enters the target */
  delay?: number;
  /** Delay before closing the popover when the mouse leaves the target, overrides the delay */
  hideDelay?: number;
  modifiers?: Modifier<string, object>[];
  strategy?: PositioningStrategy;
  preventOverflow?: boolean;
  noPaddingAndBorder?: boolean;
  /** Alternative document for click-outside events and default portal placement, used for popout windows. */
  popoverDocument?: Document;
  /** Override the max height calculated by popper */
  maxHeight?: CSSProperties['maxHeight'];
  /**
   * When true, re-schedules a delayed hover open if the popover target remounts while the pointer
   * is still over it. Opt in for virtualized lists (e.g. SearchSelect) where highlight changes
   * remount row content without a new mouseenter. Defaults to false for all other popover consumers.
   */
  resumeHoverOnTargetMount?: boolean;
  /**
   * Keeps the popover open while focus is inside the content, even if layout changes fire
   * mouseleave. The popover still closes when focus leaves the content.
   */
  keepOpenWhileContentFocused?: boolean;
}

export type PopoverProps = React.PropsWithChildren<
  ReMap<
    Omit<PopoverState, 'update' | 'close' | 'open' | 'toggle' | 'placement' | 'popoverDocument'> &
      Omit<PopoverContentProps, 'transitionStyle'>
  >
>;

export type PopoverState = {
  onClickTarget: ((e: Event) => void) | (() => void);
  onMouseEnterTarget: MouseEventHandler<HTMLSpanElement> | undefined;
  onMouseEnterContent: MouseEventHandler<HTMLDivElement> | undefined;
  onMouseLeave: MouseEventHandler<HTMLDivElement> | undefined;
  onFocusTarget: (() => void) | undefined;
  onBlurTarget: FocusEventHandler<HTMLSpanElement> | undefined;
  onFocusContent: FocusEventHandler<HTMLDivElement> | undefined;
  onBlurContent: FocusEventHandler<HTMLDivElement> | undefined;
  targetRef: HTMLElement | null;
  contentRef: HTMLDivElement | null;
  setTargetRef: React.RefCallback<HTMLSpanElement>;
  setContentRef: React.RefCallback<HTMLDivElement>;
  tabIndex?: number;
  state: State | null;
  update: (() => Promise<Partial<State>>) | null;
  /** Amount of spacing around overflow prevention */
  spacing: number;
  /** Viewport height cap (px) for the popover content. */
  availableHeight?: number;
  close: () => void;
  open: () => void;
  isOpen: boolean;
  toggle: () => void;
  placement?: Placement;
  isSmall: boolean;
  usePortal: boolean;
  portalElement?: HTMLElement;
  styles: { popper: CSSProperties };
  attributes: { [key: string]: { [key: string]: string } | undefined };
  preventOverflow?: boolean;
  noPaddingAndBorder: boolean;
  contentStyle?: CSSProperties;
  /** Optional to set a zIndex over complex components -
   * use {@see Z_INDEX} from kyoko/src/styles/layout.ts as a basis */
  zIndex?: number;
};

export interface PopoverContentProps extends Pick<PopoverState, 'zIndex'> {
  style?: CSSProperties;
  pointerEvents?: CSSProperties['pointerEvents'];
  contentStyle?: CSSProperties;
  placement?: Placement;
  variant?: PopoverVariant;
  transitionStyle?: SpringValues<CSSProperties>;
  isSmall?: boolean;
  children: ReactNode;
  onMouseLeave?: MouseEventHandler<HTMLDivElement>;
  availableHeight?: number;
  overflowY?: CSSProperties['overflowY'];
  targetStyle?: CSSProperties;
  noPaddingAndBorder?: boolean;
  maxHeight?: CSSProperties['maxHeight'];
  'aria-busy'?: AriaAttributes['aria-busy'];
  'data-testid'?: string;
}

export const PopoverVariant = {
  Default: 'DEFAULT',
  Primary: 'PRIMARY',
} as const;
export type PopoverVariant = (typeof PopoverVariant)[keyof typeof PopoverVariant];
